/*
DoubleIncCardEditingDrawer.js
双增公式编辑Drawer
 */
import React, {useState, useRef, useEffect} from "react";
import {Drawer, Form, Input, Divider, InputNumber, Button, message, Flex} from "antd";
import {BlockMath, InlineMath} from "react-katex";
import {calDoubleIncFormula} from "./calDoubleIncFormula";
import {getDoubleIncCard, updateDoubleCardInfo} from "../../../Model/DoubleIncIndexedDB/DoubleIncIndexedDB";
import {DoubleIncFormulaIcon} from "../Images/DoubleIncFormulaIcon";

const {TextArea} = Input;

const DoubleIncCardEditingDrawer = ({isMobile, open, setOpen, drawerCardID, handleRefreshCards}) => {
    const [form] = Form.useForm();
    // 控制禁用的字段
    const [disabledFields, setDisabledFields] = useState({T1: false, T2: false, k: false});
    // 当前需要被计算的值
    const willCalParaRef = useRef(null);

    useEffect(() => {
        // 设置当前form的字段
        if (open === false) {
            return
        }
        getDoubleIncCard(drawerCardID).then((card) => {
            form.setFields([
                {name: 'card_title', value: card.title},
                {name: 'card_description', value: card.description},
                {name: 'T1', value: card.cal === 'T1' ? null : (Number.isFinite(card.T1) ? card.T1 : null)},
                {name: 'T2', value: card.cal === 'T2' ? null : (Number.isFinite(card.T2) ? card.T2 : null)},
                {name: 'k', value: card.cal === 'K' ? null : (Number.isFinite(card.k) ? card.k : null)},
            ]);
            // 如果有未填写的字段，禁用它
            const newDisabledFields = {T1: false, T2: false, k: false};
            if (card.cal !== '-') {
                newDisabledFields[card.cal] = true; // 保留未填的项
            }
            setDisabledFields(newDisabledFields);
        });
    }, [open]);

    // 关闭Drawer
    const handleDrawerClose = () => {
        handleRefreshCards();
        setOpen(false);
    };

    // 处理输入变化
    const handleFieldChange = (changedValues) => {
        // 获取所有表单项的值
        const values = form.getFieldsValue();
        let count = 0;
        // 只统计 T1, T2, T3 和 k 这四个字段
        const fieldsToCheck = ['T1', 'T2', 'k'];

        fieldsToCheck.forEach(field => {
            if (values[field] !== undefined && values[field] !== '' && values[field] !== null) {
                count += 1;
            }
        });

        // 当填写2项时，禁用剩余的输入框
        if (count >= 2) {
            // 找到未填写的字段
            const emptyField = fieldsToCheck.find(field => !values[field]);
            willCalParaRef.current = emptyField;
            // 如果有未填写的字段，禁用它
            const newDisabledFields = {T1: false, T2: false, k: false};
            if (emptyField) {
                newDisabledFields[emptyField] = true; // 保留未填的项
            }
            setDisabledFields(newDisabledFields);
        } else {
            setDisabledFields({T1: false, T2: false, k: false});
        }
    };

    const handleFormFinish = (value) => {
        // 判断是否现在有两项已知项, 只统计 T1, T2和 k 这四个字段
        const fieldsToCheck = ['T1', 'T2', 'k'];
        let count = 0;
        fieldsToCheck.forEach(field => {
            if (value[field] !== undefined && value[field] !== '' && value[field] !== null) {
                count += 1;
            }
        });
        
        if (count !== 2) {
            message.error('请选取其中两项，并填入有效数字').then();
            return;
        }
        // 创建一个数组用于存储所有异步请求的 Promise
        const promises = [];
        // 计算另外一个参数的值
        switch (willCalParaRef.current) {
            case 'k':
                const k = calDoubleIncFormula(value.T1, value.T2, undefined);
                const k_100 = parseFloat((k * 100).toFixed(2)); // 保留两位小数
                // 更新卡片信息
                promises.push(
                    updateDoubleCardInfo(drawerCardID, value.card_title, value.card_description, value.T1, value.T2, k_100, 'k'));
                break;
            case 'T1':
                const t1 = calDoubleIncFormula(undefined, value.T2, value.k);
                promises.push(updateDoubleCardInfo(drawerCardID, value.card_title, value.card_description, t1, value.T2, value.k, 'T1'));
                break;
            case 'T2':
                const t2 = calDoubleIncFormula(value.T1, undefined, value.k);
                promises.push(updateDoubleCardInfo(drawerCardID, value.card_title, value.card_description, value.T1, t2, value.k, 'T2'));
                break;
            default:
                console.log('unknown para');
        }
        // 使用 Promise.all 等待所有异步操作完成
        Promise.all(promises)
            .then(() => {
                // 异步操作完成后调用刷新函数和关闭操作
                handleRefreshCards();
                setOpen(false);
            })
            .catch((error) => {
                console.error("Error while updating card info:", error);
            });
    };

    return (
        <Drawer title="参数设定" placement='left' width={isMobile ? '100%' : '20%'} open={open}
                onClose={handleDrawerClose} maskClosable={false}>
            {isMobile &&
                <Flex justify='center' align='center'>
                    <DoubleIncFormulaIcon style={{width: isMobile ? '90%' : '60%', height: 'auto'}}/>
                </Flex>
            }
            <Form form={form} onValuesChange={handleFieldChange} onFinish={handleFormFinish}
                  initialValues={{T3: 20}} style={{marginTop: 20}}>
                <Form.Item name='card_title' label='名称'>
                    <TextArea placeholder='请输入名称' showCount maxLength='10'
                              allowClear autoSize/>
                </Form.Item>
                <Form.Item name='card_description' label='备注'>
                    <TextArea placeholder='请输入名称' showCount maxLength='30'
                              allowClear autoSize/>
                </Form.Item>
                <Form.Item>
                    <Divider style={{borderColor: '#C0C0C0'}}/>
                </Form.Item>
                <Form.Item name='T1' label={<BlockMath math={`{t_{1}}^{\\ast }`}/>}>
                    <InputNumber suffix="℃" min={-273.15} max={1000} variant="filled" style={{width: '100%'}}
                                 disabled={disabledFields.T1}/>
                </Form.Item>
                <Form.Item name='T2' label={<BlockMath math={`{t_{2}}^{\\ast }`}/>}>
                    <InputNumber suffix="℃" min={-273.15} max={1000} variant="filled" style={{width: '100%'}}
                                 disabled={disabledFields.T2}/>
                </Form.Item>
                <Form.Item name='T3' label={<BlockMath math={`{t_{3}}^{\\ast }`}/>}>
                    <InputNumber suffix="℃" variant="filled" style={{width: '100%'}}
                                 disabled={true}/>
                </Form.Item>
                <Form.Item name='k' label={<BlockMath math={`{K}^{\\ast \\ast }`}/>}>
                    <InputNumber suffix="%" min={0} max={200} variant="filled" style={{width: '100%'}}
                                 disabled={disabledFields.k}/>
                </Form.Item>
                <Form.Item>
                    <p style={{color: '#808080'}}><InlineMath math={`{ }^{\\ast }`}/> <InlineMath
                        math={`t_{1}`}/>和<InlineMath math={`t_{2}`}/>的取值范围均为
                        -273.15℃ ~ 1000℃。<InlineMath math={`t_{3}`}/>为常值20℃。</p>
                    <p style={{color: '#808080'}}><InlineMath math={`{ }^{\\ast \\ast }`}/> <InlineMath math={`K`}/>的可输入范围为
                        0% ~ 200%</p>
                </Form.Item>
                <Form.Item style={{textAlign: 'right'}}>
                    <Button type='primary' htmlType='submit'>确定</Button>
                </Form.Item>
            </Form>
        </Drawer>
    );
};

export default DoubleIncCardEditingDrawer;